// Subscribes admins to live INSERTs on the feedback table.
// Behavior:
//  - Toasts when a new submission arrives (with link to /admin/feedback).
//  - Tracks an unread count we expose for badges.
//  - Persists "last seen" feedback timestamp in localStorage so the badge
//    survives reloads but resets the moment an admin opens the inbox.
//  - Suppresses toast (still increments badge) while the admin is already on
//    /admin/feedback to avoid noise.
import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { toast } from "sonner";

const LAST_SEEN_KEY = "hl:admin_feedback_last_seen:v1";

type FeedbackEvent = {
  id: string;
  subject: string;
  category: string;
  severity: string;
  created_at: string;
};

function readLastSeen(): string {
  if (typeof window === "undefined") return new Date(0).toISOString();
  try {
    return window.localStorage.getItem(LAST_SEEN_KEY) ?? new Date(0).toISOString();
  } catch {
    return new Date(0).toISOString();
  }
}

function writeLastSeen(ts: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LAST_SEEN_KEY, ts);
  } catch {
    // ignore
  }
}

export function useNewFeedbackNotifier() {
  const { isAdmin, loading } = useUserRole();
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  const onFeedbackPage = location.pathname.startsWith("/admin/feedback");

  // Reset badge whenever the admin opens the inbox
  useEffect(() => {
    if (onFeedbackPage) {
      writeLastSeen(new Date().toISOString());
      setUnreadCount(0);
    }
  }, [onFeedbackPage]);

  // Initial unread count: feedback created since last_seen
  useEffect(() => {
    if (loading || !isAdmin) return;
    if (onFeedbackPage) return; // we just zeroed it
    let cancelled = false;
    const since = readLastSeen();
    supabase
      .from("feedback")
      .select("id", { count: "exact", head: true })
      .gt("created_at", since)
      .then(({ count, error }) => {
        if (cancelled || error) return;
        setUnreadCount(count ?? 0);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, loading, onFeedbackPage]);

  // Realtime subscription
  useEffect(() => {
    if (loading || !isAdmin) return;

    const channel = supabase
      .channel("admin-feedback-notifier")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "feedback" },
        (payload) => {
          const row = payload.new as Partial<FeedbackEvent> | null;
          if (!row?.id) return;

          setUnreadCount((c) => c + 1);

          if (onFeedbackPage) return; // don't toast — admin is already viewing

          const sevPrefix =
            row.severity === "critical"
              ? "🔥 Critical · "
              : row.severity === "high"
                ? "⚠️ High · "
                : "";
          toast(`${sevPrefix}New ${row.category ?? "feedback"}`, {
            description: row.subject?.slice(0, 120) ?? "New feedback submitted",
            action: {
              label: "Triage",
              onClick: () => navigate("/admin/feedback"),
            },
            duration: 8000,
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdmin, loading, onFeedbackPage, navigate]);

  const markAllRead = useCallback(() => {
    writeLastSeen(new Date().toISOString());
    setUnreadCount(0);
  }, []);

  return { unreadCount, markAllRead };
}
